import { Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpErrorResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { environment } from '@/../environments/environment';
import { TipoDocumento, TipoDocumentoRequest } from '@/models';
import { PageResponse, StatusCount } from './pagination.types';

export interface DeleteTipoDocumentoError {
  message: string;
  editalNome?: string;
}

@Injectable({
  providedIn: 'root',
})
export class TipoDocumentoService {
  private baseUrl = `${environment.apiUrl}/admin/tipos-documentos`;

  constructor(private http: HttpClient) {}

  getTiposDocumentos(): Observable<TipoDocumento[]> {
    return this.http.get<TipoDocumento[]>(this.baseUrl);
  }

  getTipoDocumento(id: number): Observable<TipoDocumento> {
    return this.http.get<TipoDocumento>(`${this.baseUrl}/${id}`);
  }

  createTipoDocumento(data: TipoDocumentoRequest): Observable<TipoDocumento> {
    return this.http.post<TipoDocumento>(this.baseUrl, data);
  }

  updateTipoDocumento(id: number, data: TipoDocumentoRequest): Observable<TipoDocumento> {
    return this.http.put<TipoDocumento>(`${this.baseUrl}/${id}`, data);
  }

  deleteTipoDocumento(id: number): Observable<void> {
    return this.http.delete<void>(`${this.baseUrl}/${id}`).pipe(
      catchError((error: HttpErrorResponse) => {
        // Se o erro contém informações sobre o edital vinculado
        if (error.error && error.error.editalNome) {
          const customError: DeleteTipoDocumentoError = {
            message: error.error.message || 'Não é possível excluir este tipo de documento pois está vinculado a um edital',
            editalNome: error.error.editalNome,
          };
          return throwError(() => customError);
        }
        return throwError(() => error);
      })
    );
  }

  toggleStatus(id: number, active: boolean): Observable<TipoDocumento> {
    return this.http.patch<TipoDocumento>(`${this.baseUrl}/${id}/toggle-status`, { active });
  }

  /**
   * Lista tipos de documentos paginados
   * GET /api/admin/tipos-documentos/paginado
   */
  listarPaginado(
    page: number = 0,
    size: number = 10,
    sortBy: string = 'nome',
    sortDirection: 'ASC' | 'DESC' = 'ASC',
    apenasAtivos?: boolean
  ): Observable<PageResponse<TipoDocumento>> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sortBy', sortBy)
      .set('sortDirection', sortDirection);
    
    if (apenasAtivos !== undefined) {
      params = params.set('apenasAtivos', apenasAtivos.toString());
    }
    
    return this.http.get<PageResponse<TipoDocumento>>(`${this.baseUrl}/paginado`, { params });
  }

  /**
   * Obtém contagem de status dos tipos de documentos
   * GET /api/admin/tipos-documentos/status
   */
  getStatus(): Observable<StatusCount> {
    return this.http.get<StatusCount>(`${this.baseUrl}/status`);
  }
}
